import { standardSanitizeHtml } from '../components/utilities/markdown/standard-sanitize-html';

/**
 * Helper functions for preprocessing markdown content before rendering
 * to ensure consistent line breaks and proper formatting of code blocks.
 */

/**
 * Preprocesses markdown content to handle escaped newlines, code blocks,
 * and general line breaks for proper rendering.
 *
 * @param content The markdown content to preprocess
 * @returns Processed markdown with proper spacing and line breaks
 */
export const preprocessMarkdown = (content: string): string => {
  if (!content) {
    return '';
  }

  let processed = standardSanitizeHtml(content);

  // Handle escaped newlines (\\n) by replacing with Markdown line breaks
  processed = processed.replace(/\\n/g, '  \n');

  // Handle escaped backslashes (\\) by converting to single backslash
  processed = processed.replace(/\\\\/g, '\\');

  // In markdown a \ at the end of a line is a linebreak
  processed = processed.replace(/\s\\\n/g, '\n\n');

  // Find code blocks and ensure they have proper spacing
  const codeBlockRegex = /(```[\s\S]*?```)/g;
  const parts = processed.split(codeBlockRegex);

  const result: string[] = [];

  for (let i = 0; i < parts.length; i++) {
    if (parts[i].startsWith('```') && parts[i].endsWith('```')) {
      // This is a code block - preserve as is
      if (i > 0 && !result[result.length - 1].endsWith('\n\n')) {
        // Ensure there are exactly two newlines before code block
        if (result[result.length - 1].endsWith('\n')) {
          result[result.length - 1] = result[result.length - 1] + '\n';
        } else {
          result[result.length - 1] = result[result.length - 1] + '\n\n';
        }
      }

      // Add the code block unchanged
      result.push(parts[i]);

      // Prepare for content after code block
      if (i < parts.length - 1 && !parts[i + 1].startsWith('\n\n')) {
        result.push('\n\n');
      }
    } else if (parts[i].trim()) {
      // Regular text - preserve as is
      result.push(parts[i]);
    } else {
      // Empty part, just add it
      result.push(parts[i]);
    }
  }

  return result.join('');
};

/**
 * Wraps code in markdown code block syntax
 *
 * @param code The code to wrap in a markdown code block
 * @returns The code wrapped in markdown code block delimiters
 */
export const markdownCodeBlock = (code: string): string =>
  `\`\`\`\n${code}\n\`\`\``;
